import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from './api';

/**
 * Loads data on mount and gives back a reload handle. Guards against setting
 * state after the screen has gone - a real risk on a phone where people
 * navigate away mid-request.
 */
export function useLoad<T>(loader: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const alive = useRef(true);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const result = await loaderRef.current();
      if (!alive.current) return;
      setData(result);
      setError(null);
    } catch (err) {
      if (!alive.current) return;
      setError(err instanceof ApiError ? err.message : 'Could not load this.');
    } finally {
      if (alive.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    reload();
    return () => {
      alive.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, setData, error, loading, reload };
}

/** Tracks an in-flight mutation and surfaces its error message. */
export function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong.');
      return undefined;
    } finally {
      setBusy(false);
    }
  }, []);

  return { busy, error, setError, run };
}

/**
 * Debounced autosave. Inspections are long, phones lock, signal drops - so
 * marks are pushed a moment after the fitter stops tapping rather than on a
 * save button they might never reach.
 */
export function useAutosave<T>(
  save: (payload: T) => Promise<unknown>,
  {
    delay = 900,
    maxWait = 4000,
    merge,
  }: {
    delay?: number;
    maxWait?: number;
    /**
     * Combines a queued payload with the one already waiting. Required for
     * anything queued in rapid succession - without it the later call simply
     * replaces the earlier one and that work is never sent.
     */
    merge?: (previous: T, next: T) => T;
  } = {},
) {
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const pending = useRef<T | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deadline = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveRef = useRef(save);
  const mergeRef = useRef(merge);
  saveRef.current = save;
  mergeRef.current = merge;

  const clearTimers = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (deadline.current) {
      clearTimeout(deadline.current);
      deadline.current = null;
    }
  };

  const flush = useCallback(async () => {
    clearTimers();
    const payload = pending.current;
    if (payload == null) return;
    pending.current = null;
    setState('saving');
    try {
      await saveRef.current(payload);
      // Anything queued while this request was in flight must survive.
      setState(pending.current ? 'saving' : 'saved');
    } catch {
      setState('error');
      // Put it back - merged under anything queued meanwhile, so a retry
      // carries both rather than clobbering the newer work.
      pending.current =
        pending.current && mergeRef.current
          ? mergeRef.current(payload, pending.current)
          : (pending.current ?? payload);
    }
  }, []);

  const queue = useCallback(
    (payload: T) => {
      pending.current =
        pending.current && mergeRef.current
          ? mergeRef.current(pending.current, payload)
          : payload;
      setState('saving');

      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(flush, delay);

      // Steady tapping keeps resetting the debounce, so a hard ceiling
      // guarantees work reaches the server while an inspection is running.
      if (!deadline.current) deadline.current = setTimeout(flush, maxWait);
    },
    [delay, maxWait, flush],
  );

  // Last chance to persist when the tab is hidden or closed.
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    document.addEventListener('visibilitychange', onHide);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      flush();
    };
  }, [flush]);

  return { queue, flush, state, hasPending: () => pending.current != null };
}

/**
 * Pull down to refresh.
 *
 * The app disables the browser's own overscroll (a half-finished inspection
 * must not be thrown away by a stray swipe), so this supplies the gesture
 * deliberately: it only engages at the very top of the page, resists as it
 * stretches, and fires once past the threshold.
 */
export function usePullToRefresh(onRefresh?: () => void | Promise<unknown>) {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const start = useRef<number | null>(null);
  const handler = useRef(onRefresh);
  handler.current = onRefresh;

  const THRESHOLD = 72;
  const MAX = 110;

  useEffect(() => {
    if (!onRefresh) return;

    const atTop = () => (window.scrollY || document.documentElement.scrollTop) <= 0;

    const onStart = (e: TouchEvent) => {
      if (!atTop() || refreshing || e.touches.length !== 1) return;
      start.current = e.touches[0].clientY;
    };

    const onMove = (e: TouchEvent) => {
      if (start.current === null) return;
      const delta = e.touches[0].clientY - start.current;

      // An upward swipe, or leaving the top, hands the gesture back to the page.
      if (delta <= 0 || !atTop()) {
        start.current = null;
        setPull(0);
        return;
      }
      // Resistance: the further it is pulled, the less it gives.
      const distance = Math.min(MAX, delta * 0.45);
      setPull(distance);
      if (e.cancelable) e.preventDefault();
    };

    const onEnd = async () => {
      if (start.current === null) return;
      const reached = pull >= THRESHOLD;
      start.current = null;

      if (!reached) {
        setPull(0);
        return;
      }
      setRefreshing(true);
      setPull(THRESHOLD);
      try {
        await handler.current?.();
      } finally {
        setRefreshing(false);
        setPull(0);
      }
    };

    // passive:false so the move handler can hold the page still while pulling.
    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);
    window.addEventListener('touchcancel', onEnd);
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, [onRefresh, pull, refreshing]);

  return { pull, refreshing, ready: pull >= THRESHOLD, threshold: THRESHOLD };
}

/** Value that only updates once typing pauses - used for search boxes. */
export function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}
